// What the client said, and what the opportunity is worth, in words (pipeline P9, REQ-2026-10-05-14;
// CLIENT-WORDS-AND-VALUE-BRIEF-2026-10-05.md section 4, drawn in screens/2026-10-05-add-deal-concepts/c-card.html; module
// 10 U292 to U297). Mike, 5 Oct 2026: "'what the client said' There is an under appreciation of the important of this in
// deals and this also inckudes the subsequent communication tracking".
//
// Pure: the line's latest words and silence, the card's keenness line, "In their words", and the Value block's rows and
// sentences, each from the heat read alone, so every sentence is testable without a page.
import { dayMonth, monthShort } from '../day.js';
import type { CobrainMove, DealMessage, DealValue, EarlierValue, HeatRow, Named, PayTerms, SaidLine, ValueMove } from './api.js';
import { firstName, moneyFull, possessive } from './heat-words.js';
import { dayWords } from './live-words.js';

export type Channel = SaidLine['channel'];

/** The record's chip: Mail, Teams, Call, Meeting, Notes (c-card.html). */
export function channelChip(c: Channel): string {
  return c === 'mail' ? 'Mail' : c === 'teams' ? 'Teams' : c === 'call' ? 'Call' : c === 'meeting' ? 'Meeting' : 'Notes';
}

/** A message's kind as the record names its channel: email is mail, a chat or a channel post is Teams. */
export function channelOf(kind: string): Channel {
  if (kind === 'email') return 'mail';
  if (kind.startsWith('teams')) return 'teams';
  if (kind === 'call') return 'call';
  if (kind === 'meeting') return 'meeting';
  return 'notes';
}

/** The channel as a thing someone sent: "Ana Ruiz's Teams message", "the call". */
export function channelNoun(c: Channel): string {
  return c === 'mail' ? 'mail' : c === 'teams' ? 'Teams message' : c === 'call' ? 'call' : c === 'meeting' ? 'meeting' : 'notes';
}

/** "that call", "that Teams message", "those notes". */
const thatOne = (c: Channel | null): string => (c === null ? 'that message' : c === 'notes' ? 'those notes' : `that ${channelNoun(c)}`);

/** "Open the call", "Open the mail", "Open the chat" (c-card.html). */
export function openWords(c: Channel): string {
  return c === 'teams' ? 'Open the chat' : c === 'notes' ? 'Open the notes' : `Open the ${c}`;
}

const at = (iso: string, dayStart: string | null) => dayWords(iso, dayStart);
/** "of 5 Oct", or "today" (never "of today"). */
const ofDay = (iso: string, dayStart: string | null) => { const d = dayWords(iso, dayStart); return d === 'today' ? 'today' : `of ${d}`; };
/** Where a message was: "on the call of 1 Oct", "in the mail of 5 Oct", "in the Teams message of 5 Oct", "in the notes today". */
const placeOf = (c: Channel, iso: string, dayStart: string | null) =>
  `${c === 'call' || c === 'meeting' ? 'on the' : 'in the'} ${channelNoun(c)} ${ofDay(iso, dayStart)}`;
const quoted = (w: string) => `“${w.trim().replace(/^["“”']+|["“”']+$/g, '')}”`;

/** Their words as the line and the record show them: in quotes, the sentence that carries the point, never cut mid-word. */
export function theirWords(w: string, max = 240): string {
  const t = w.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return quoted(t);
  const cut = t.slice(0, max);
  return quoted(`${cut.slice(0, Math.max(cut.lastIndexOf(' '), max - 20))}…`);
}

/** The words the line carries under its status, with their source: the record's newest of theirs, else the sheet's. */
export interface LineWords { words: string; source: string }

/**
 * The line's latest words (U296): "“The share works for us…”" with "Ana Ruiz, call, 8 Oct"; from a silent deal, "their
 * last, mail, 3 Sep". Before the record holds anything, the words that set the keenness, from the message or the notes.
 */
export function lineWords(row: HeatRow, showName: string | null, dayStart: string | null, silent: boolean): LineWords | null {
  const last = row.live?.theirLast ?? null;
  const lead = silent ? 'their last, ' : '';
  if (last) {
    const who = last.who && !silent ? `${last.who}, ` : '';
    return { words: theirWords(last.words, 160), source: `${lead}${who}${channelNoun(last.channel) === 'Teams message' ? 'Teams' : channelNoun(last.channel)}, ${at(last.at, dayStart)}` };
  }
  const cw = row.clientWords;
  if (!cw) return null;
  if (cw.from === 'signal') {
    const c = channelOf(cw.kind ?? '');
    return { words: theirWords(cw.text, 160), source: `${lead}${c === 'teams' ? 'Teams' : channelNoun(c)}${cw.at ? `, ${at(cw.at, dayStart)}` : ''}` };
  }
  return { words: theirWords(cw.text, 160), source: showName ? `${showName} notes` : 'from the notes' };
}

/** "Silent for 12 days since our proposal" once the proposal is out; "Silent for 12 days" before it (c-card.html). */
export function silenceWords(days: number, stageCode: string | null): string {
  const n = `${days} ${days === 1 ? 'day' : 'days'}`;
  return stageCode === 'paper_out' ? `Silent for ${n} since our proposal` : `Silent for ${n}`;
}

/** The card's keenness line (U293, U296): what it is, what moved it, and the words that did. */
export interface KeennessLine {
  paragraph?: string;
  meetingId?: string;
  text: string;
  /** The message that set it, to open; the move to undo, while it can be. */
  message: DealMessage | null;
  move: CobrainMove | null;
}

const whoOf = (by: Named | null | undefined): string => (by?.mine ? 'you' : firstName(by ?? null) ?? 'a colleague');

/**
 * "Keenness 4, was 3 until Ana Ruiz's Teams message of 6 Oct: “We'd like to move on One Data before the new season.”"
 * (c-card.html). A person's: "Keenness 4, set by you, 3 Oct: “asked for terms”", with Cobrain's own reading kept beside
 * it (U232). The notes': "Keenness 4, from the SBC Summit 2026 notes: “…”". Put back: who put it back and that Cobrain
 * will not move it again from that message.
 */
export function keennessLine(row: HeatRow, showName: string | null, dayStart: string | null): KeennessLine {
  const k = row.keenness;
  const live = row.live;
  if (k === null) return { text: 'No keenness yet.', message: null, move: null };
  const words = row.clientWords ? theirWords(row.clientWords.text) : null;
  const m = live?.keennessMove ?? (live?.lastMove?.field === 'keenness' ? live.lastMove : null);
  if (m && m.undone && m.fromCode === String(k)) {
    const who = m.undone.by?.mine ? 'You' : firstName(m.undone.by) ?? 'Someone';
    return { text: `Keenness ${k}. ${who} put it back ${at(m.undone.at, dayStart)}; Cobrain will not move it again from ${thatOne(m.from ? channelOf(m.from.kind) : null)}.`, message: m.from, move: null };
  }
  if (row.keennessSetBy === 'cobrain' && m && !m.undone && m.toCode === String(k)) {
    if (m.call) return {
      text: `Keenness ${k}, was ${m.fromName} until the call with ${row.accountName ?? 'the account'} ${ofDay(m.call.at, dayStart)}:`,
      paragraph: m.call.paragraph, meetingId: m.call.meetingId, message: null, move: m,
    };
    if (m.fromCall) return {
      text: `Keenness ${k}, was ${m.fromName} until a call ${ofDay(m.callAt ?? m.at, dayStart)}.`,
      message: null, move: m,
    };
    const src = m.from
      ? `${m.from.fromMe ? 'your' : m.from.from ? possessive(m.from.from) : 'a'} ${channelNoun(channelOf(m.from.kind))} ${ofDay(m.from.at, dayStart)}`
      : `a message ${ofDay(m.at, dayStart)}`;
    const was = m.fromName !== null ? `, was ${m.fromName} until ${src}` : `, from ${src}`;
    return { text: `Keenness ${k}${was}${words ? `: ${words}` : '.'}`, message: m.from, move: m };
  }
  if (row.keennessSetBy === 'person') {
    const kb = live?.keennessBy ?? null;
    const why = kb?.why ? `: ${quoted(kb.why)}` : '.';
    const c = kb?.cobrain && kb.cobrain.keenness !== k ? ` Cobrain read ${kb.cobrain.keenness}${kb.cobrain.fromCall ? ' from the call' : ''} and keeps ${kb.by?.mine ? 'yours' : `${possessive(firstName(kb.by) ?? 'theirs')}`}.` : '';
    return { text: `Keenness ${k}, set by ${whoOf(kb?.by)}${row.keennessAt ? `, ${at(row.keennessAt, dayStart)}` : ''}${why}${c}`, message: null, move: null };
  }
  if (row.keennessSetBy === 'sheet') return { text: `Keenness ${k}, from the ${showName ? `${showName} ` : ''}notes${words ? `: ${words}` : '.'}`, message: null, move: null };
  return { text: `Keenness ${k}, read by Cobrain${row.keennessAt ? `, ${at(row.keennessAt, dayStart)}` : ''}${words ? `: ${words}` : '.'}`, message: null, move: null };
}

// ---------------------------------------------------------------------------------------------------------------------
// Value (U294, U295; c-card.html's Value and "Value, as it moves").

const OF: Record<string, string> = { ggr: 'GGR', ngr: 'NGR', turnover: 'turnover', net_revenue: 'net revenue' };
const pct = (p: string | number): string => `${Number(p).toLocaleString('en-GB', { maximumFractionDigits: 3 })}%`;
const per = (period: string | null | undefined): string => (period === 'month' ? 'a month' : period === 'quarter' ? 'a quarter' : 'a year');
const money = (a: string | number, c: string | null): string => moneyFull(String(a), c);

/** What the base is called: "Their NGR" (c-card.html), by what the share is of. */
export function baseLabel(pay: PayTerms | null): string {
  return `Their ${OF[pay?.shareOf ?? ''] ?? 'base'}`;
}

/** "Revenue share, 20% of NGR", "Fixed fee, £120,000 a year", "Per match, £500 a match, about 300 matches a year", "Not known yet". */
export function howItPays(pay: PayTerms | null): string {
  if (!pay || pay.basis === 'unknown') return 'Not known yet';
  if (pay.basis === 'fixed' && pay.feeAmount) return `Fixed fee, ${money(pay.feeAmount, pay.currency)} ${per(pay.feePeriod)}`;
  if (pay.basis === 'share' && pay.sharePct) return `Revenue share, ${pct(pay.sharePct)} of ${OF[pay.shareOf ?? ''] ?? 'revenue'}`;
  if (pay.basis === 'per_match' && pay.matchRate) {
    return `Per match, ${money(pay.matchRate, pay.currency)} a match${pay.matchCount ? `, about ${pay.matchCount} matches a year` : ''}`;
  }
  return 'Not known yet';
}

/** "£120,000 a year, as proposed" until the deal is signed. */
export function minimumWords(pay: PayTerms | null, stageCode: string | null): string | null {
  if (!pay?.minimumAmount) return null;
  return `${money(pay.minimumAmount, pay.currency)} ${per(pay.minimumPeriod)}${stageCode === 'signed' ? ', signed' : ', as proposed'}`;
}

/** "£900,000 a year, Ana Ruiz on the call of 8 Oct"; a person's: "£900,000 a year, set by you, 3 Oct". */
export function baseWords(v: DealValue, dayStart: string | null): string | null {
  const b = v.base;
  if (!b) return null;
  const amount = `${money(b.amount, v.pay?.currency ?? null)} ${per(b.period)}`;
  if (b.setBy === 'person') return `${amount}, set by ${whoOf(b.by)}${b.at ? `, ${at(b.at, dayStart)}` : ''}`;
  if (!b.message) return amount;
  return `${amount}, ${b.who ? `${b.who} ` : ''}${placeOf(channelOf(b.message.kind), b.message.at, dayStart)}`;
}

const monthName = (ym: string): string => new Date(`${ym}-01T12:00:00Z`).toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' });
const monthShortName = (ym: string): string => monthShort(new Date(`${ym}-01T12:00:00Z`), 'utc');

/** The statements row: "Sep: £15,000 share" (c-card.html). */
export function statementWords(v: DealValue, dayStart: string | null): string | null {
  if (v.statements.length === 0) return null;
  return v.statements.map((x) => `${x.covers ? monthShortName(x.covers) : at(x.at, dayStart)}: ${money(x.amount, x.currency)} share${x.base ? `, on ${money(x.base, x.currency)} ${OF[v.pay?.shareOf ?? ''] ?? 'base'}` : ''}`).join('; ');
}

const TIMES_WORDS: Record<number, string> = { 12: 'times twelve', 4: 'times four' };

/** How Cobrain worked the year out, from the sum it kept (migration 208's revenue_sum), naming who gave it and where. */
export function sumWords(m: ValueMove, dayStart: string | null): string {
  const s = m.sum ?? {};
  const cur = typeof s.currency === 'string' ? s.currency : m.toCurrency;
  const c = m.message ? channelOf(m.message.kind) : null;
  const iso = m.message ? m.message.at : m.at;
  const when = at(iso, dayStart);
  const where = c === null ? ofDay(iso, dayStart) : placeOf(c, iso, dayStart);
  const line = m.words ? `, ${theirWords(m.words, 160)}` : '';
  switch (s.kind) {
    case 'share_of_base': {
      const base = `${money(String(s.base_amount), cur)}${s.base_period === 'month' ? ' a month' : ''} ${OF[String(s.of ?? '')] ?? 'base'}`;
      return `${pct(String(s.share_pct))} of the ${base}${m.who ? ` ${m.who}` : ''} gave ${where}`;
    }
    case 'statement': {
      const whose = typeof s.covers === 'string' ? `${monthName(s.covers)}'s share` : s.period === 'quarter' ? "the quarter's share" : s.period === 'year' ? "the year's share" : "the month's share";
      const times = TIMES_WORDS[Number(s.times)] ? ` ${TIMES_WORDS[Number(s.times)]}` : '';
      const how = c === 'mail' ? (when === 'today' ? 'mailed today' : `mailed on ${when}`) : where;
      return `${whose} of ${money(String(s.share_amount), cur)}${times}, from their statement ${how}`;
    }
    case 'colleague_figure': {
      const place = c === 'teams' ? `the team's Teams chat ${ofDay(iso, dayStart)}` : `the ${channelNoun(c ?? 'notes')} ${ofDay(iso, dayStart)}`;
      return `${m.who ? possessive(m.who) : 'a colleague’s'} figure in ${place}${line}`;
    }
    case 'their_figure':
      return `${m.who ? possessive(m.who) : 'their'} own figure ${where}${line}`;
    default:
      return `from ${where}`;
  }
}

/** Under Value, the last move, its states drawn in c-card.html: moved, held at the minimum, and after Undo. */
export interface MoveNote { text: string; open: string | null; undo: boolean }

export function moveNote(v: DealValue, row: HeatRow, now: string, dayStart: string | null): MoveNote | null {
  const m = v.move;
  if (!m) return null;
  const c = m.message ? channelOf(m.message.kind) : null;
  const open = m.message ? openWords(c!) : null;
  if (m.undone) {
    const back = m.fromAmount !== null ? `Back to ${money(m.fromAmount, m.fromCurrency)} a year.` : 'Back to no figure.';
    // A person who has set the figure since has made it theirs: the note has nothing left to say.
    const now2 = row.figure?.amount ?? null;
    if ((now2 === null) !== (m.fromAmount === null) || (now2 !== null && m.fromAmount !== null && Number(now2) !== Number(m.fromAmount))) return null;
    // c-card.html's "After Undo" names the message and links nothing.
    return { text: `${back} Cobrain won't move the value on ${thatOne(c)} again.`, open: null, undo: false };
  }
  const s = m.sum ?? {};
  const to = m.toAmount !== null ? money(m.toAmount, m.toCurrency) : 'no figure';
  if (s.held_at_minimum === true) {
    const worked = money(String(s.worked_out), m.toCurrency);
    const what = s.kind === 'share_of_base'
      ? `${pct(String(s.share_pct))} of ${money(String(s.base_amount), m.toCurrency)}${s.base_period === 'month' ? ' a month' : ''} ${OF[String(s.of ?? '')] ?? 'base'}`
      : sumWords({ ...m, sum: { ...s, held_at_minimum: undefined } }, dayStart);
    return { text: `${what} is ${worked}, under the minimum, so we expect the minimum, ${to} a year.`, open, undo: undoableValue(m, now) };
  }
  const from = m.fromAmount !== null ? `from ${money(m.fromAmount, m.fromCurrency)} to ${to} a year` : `at ${to} a year`;
  return { text: `Cobrain ${m.fromAmount !== null ? 'moved' : 'set'} what we expect ${from}: ${sumWords(m, dayStart)}.`, open, undo: undoableValue(m, now) };
}

/** D62's week, on a move nobody has put back. */
export function undoableValue(m: ValueMove, now: string): boolean {
  return m.undone === null && m.undoUntil !== null && m.undoUntil > now;
}

/** "Earlier values: £100,000 set by you, 3 Oct; £180,000 from the call of 8 Oct, undone." */
export function earlierWords(earlier: readonly EarlierValue[], showName: string | null, dayStart: string | null): string | null {
  const parts = earlier.filter((e) => !e.undo).map((e) => {
    const amount = e.amount !== null ? money(e.amount, e.currency) : 'No figure';
    if (e.setBy === 'cobrain') {
      const c = e.message ? channelOf(e.message.kind) : null;
      return `${amount} from ${c ? `the ${channelNoun(c)} ${ofDay(e.message!.at, dayStart)}` : `Cobrain, ${at(e.at, dayStart)}`}${e.undone ? ', undone' : ''}`;
    }
    if (e.setBy === 'sheet') return `${amount} from the ${showName ? `${showName} ` : ''}notes`;
    if (e.setBy === 'person') return `${amount} set by ${whoOf(e.by)}, ${at(e.at, dayStart)}${e.undone ? ', undone' : ''}`;
    return `${amount}, ${at(e.at, dayStart)}`;
  });
  return parts.length > 0 ? `Earlier values: ${parts.join('; ')}.` : null;
}

/**
 * Who set the figure "We expect" shows (BUGS.md D151), in the words Earlier values and the base already use: a person's
 * "set by you, 3 Oct" or "set by Will, 3 Oct"; the notes' "from the SBC Summit 2026 notes"; Cobrain's "read by Cobrain,
 * 3 Oct" unless the move note under it already says so (`moveSaid`). Null when there is no figure.
 */
export function figureByWords(f: HeatRow['figure'], moveSaid: boolean, showName: string | null, dayStart: string | null): string | null {
  if (!f) return null;
  if (f.setBy === 'person') return `set by ${whoOf(f.by)}, ${at(f.at, dayStart)}`;
  if (f.setBy === 'sheet' && f.term) {
    // The team's account sheet, with the Padel Change Log's status beside the figure; only Confirmed is agreed (U429).
    const buy = f.term.buyout !== null ? `buyout £${f.term.buyout.toLocaleString('en-GB')}, ` : '';
    return `from the team's account sheet: ${buy}${f.term.status}${f.term.agreed ? '' : `, not agreed${f.term.comment ? `. ${f.term.comment}` : ''}`}`;
  }
  if (f.setBy === 'sheet') return `from the ${showName ? `${showName} ` : ''}notes`;
  if (f.setBy === 'cobrain') return moveSaid ? null : `read by Cobrain, ${at(f.at, dayStart)}`;
  return null;
}

/** The owner's question when a view had no figure (the brief, section 4): "The team thinks this year is bigger. New figure?" */
export function questionWords(q: NonNullable<DealValue['question']>): string {
  return `The team thinks this year is ${q.direction === 'higher' ? 'bigger' : 'smaller'}.`;
}

/** "Nothing from Northgate Bet on this deal yet. …" (c-card.html, "Nothing from them yet"). */
export function nothingYetWords(account: string | null): string {
  return `Nothing from ${account ?? 'them'} on this deal yet. What they say in mail, Teams, calls, meetings and your notes appears here.`;
}

/** The record's entry header: who and when ("Ana Ruiz", "8 Oct"); ours is "We said" with who sent it ("You, 7 Oct"). */
export function entryWho(l: SaidLine): string {
  return l.mine ? 'You' : l.who ?? (l.side === 'us' ? 'Our side' : 'Them');
}

/** "The whole conversation, 14 messages". */
export function conversationWords(n: number): string {
  return `The whole conversation, ${n} ${n === 1 ? 'message' : 'messages'}`;
}

/** The day a date is, for the record. */
export const recordDay = (iso: string, dayStart: string | null): string => (dayStart !== null && iso >= dayStart ? 'today' : dayMonth(new Date(iso)));
